import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { type AllowedLang, useUserInfo } from '@/context/user-info';
import { cn } from '@/lib/utils';
import { languageSelection } from '@/misc/constants';
import { ChevronDown, Globe01 } from '@untitled-ui/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface Props {
  isMobile?: boolean;
}

export const LanguageSelect = ({ isMobile }: Props) => {
  const { t } = useTranslation();
  const { userLanguage, allowedLangs, onChangeLanguage } = useUserInfo();
  const navigate = useNavigate();

  const filteredLanguageOptions = useMemo(
    () => languageSelection.filter((language) => language.value !== userLanguage),
    [userLanguage],
  );

  const handleLangChange = (newLang: AllowedLang) => {
    onChangeLanguage(newLang);
    const pathParts = window.location.pathname.split('/');
    const currentLangInUrl = allowedLangs.includes(pathParts[1] as AllowedLang)
      ? pathParts[1]
      : null;
    let newPath: string;
    if (currentLangInUrl) {
      newPath = `/${newLang}${window.location.pathname.substring(3)}`;
    } else {
      newPath = `/${newLang}${window.location.pathname}`;
    }
    navigate(newPath + window.location.search + window.location.hash, { replace: true });
  };

  return (
    <DropdownMenu aria-label={t('translation:accessibility.button.language_selector')}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn({
            'h-full group flex items-center gap-1 py-2.5 px-4 rounded-full bg-transparent hover:bg-bg-primary transition-all duration-300 w-fit':
              !isMobile,
            'gap-1': isMobile,
          })}
        >
          <Globe01
            className={cn(
              'text-fg-brand size-5 transition-opacity duration-200',
              'opacity-0',
              'group-hover:opacity-100 group-hover:animate-bounceInLeft',
            )}
          />
          <p
            className={cn({
              'text-white font-bold': true,
              'pl-0 group-hover:pl-1 transition-all duration-300 text-white group-hover:text-fg-brand':
                !isMobile,
              'text-xl uppercase': isMobile,
            })}
          >
            {t('translation:navbar.language_selector.label')}
          </p>
          <ChevronDown
            className={cn({ 'size-4 text-white': true, 'group-hover:text-fg-brand': true })}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={isMobile ? 'center' : 'end'}
        className={cn({
          'w-fit z-50': true,
          'mr-4': !isMobile,
          'bg-bg-dark text-white border-none shadow-none flex items-center justify-between':
            isMobile,
        })}
      >
        {filteredLanguageOptions.map((language) => (
          <DropdownMenuItem
            key={language.value}
            onClick={() => handleLangChange(language.value as AllowedLang)}
          >
            <p
              className={cn({
                'px-2 font-bold': true,
                'text-base': isMobile,
              })}
            >
              {t(language.label)}
            </p>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
